import { createPortal } from 'react-dom';
import { ReactNode, useEffect, useRef } from 'react';
import { useMap } from '../hooks';

export type Anchors = 'topCenter' | 'bottomCenter' | 'bestCenter';

const Y_PADDING = 12;

function anchorPosition(
    width: number,
    height: number,
    point: google.maps.Point,
    padding: number,
    anchor: Anchors
) {
    return new google.maps.Point(
        point.x - width / 2,
        anchor === 'topCenter'
            ? point.y - (height + padding)
            : point.y + padding
    );
}

export const Overlay = ({
    children,
    position,
    anchor,
    padding = Y_PADDING,
}: {
    children: ReactNode;
    anchor: Anchors;
    position: google.maps.LatLng | google.maps.LatLngLiteral;
    padding?: number;
}): JSX.Element => {
    const root = useRef(document.createElement('div'));
    root.current.setAttribute('id', 'rwl-map-overlay');
    const overlay = useRef<google.maps.OverlayView>();
    const { map } = useMap();

    useEffect(() => {
        if (!map) {
            return;
        }

        let calculatedAnchor: Anchors;

        overlay.current = Object.assign(new google.maps.OverlayView(), {
            onAdd() {
                const div = root.current;
                div.style.backgroundColor = 'transparent';
                div.style.position = 'absolute';
                div.style.left = '0px';
                div.style.top = '0px';
                div.style.width = 'auto';
                div.style.height = 'auto';

                const panes = (this as google.maps.OverlayView).getPanes()!;
                panes.floatPane.appendChild(div);

                if (anchor === 'bestCenter') {
                    // If best center, figure out if it goes on top of bottom.
                    const overlayProjection = (
                        this as google.maps.OverlayView
                    ).getProjection();

                    const pos =
                        overlayProjection.fromLatLngToDivPixel(position);
                    if (pos && pos.y > 0) {
                        calculatedAnchor = 'topCenter';
                    } else {
                        calculatedAnchor = 'bottomCenter';
                    }
                }
            },

            onRemove() {
                if (root.current.parentElement) {
                    root.current.parentElement.removeChild(root.current);
                }
            },

            draw() {
                const overlayProjection = (
                    this as google.maps.OverlayView
                ).getProjection();

                const pos = anchorPosition(
                    root.current.offsetWidth,
                    root.current.offsetHeight,
                    overlayProjection.fromLatLngToDivPixel(position)!,
                    padding,
                    anchor === 'bestCenter' ? calculatedAnchor : anchor
                );

                root.current.style.left = pos.x + 'px';
                root.current.style.top = pos.y + 'px';
            },
        });

        overlay.current.setMap(map);
    }, [map]);

    useEffect(() => {
        return () => {
            overlay.current?.setMap(null);
        };
    }, []);

    return createPortal(children, root.current);
};
